'use client';

import { logger } from '@curology/logger/client';
import { ErrorIcon } from '@curology/ui-components-web/react/Icon/icons/ErrorIcon';
import { ThumbsUpIcon } from '@curology/ui-components-web/react/Icon/icons/ThumbsUpIcon';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { DATA_KEYS } from '@/consts/dataKeys';
import {
  FunnelService,
  type CompleteSignupError,
} from '@/services/funnelService/client';
import FlowType from '@/services/funnelService/consts/flowType';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import { useDataProvider } from '../../components/DataProvider/useDataProvider';
import { useFlowContext } from '../../hooks/useFlowContext';

export const CompleteSignupSideEffects = () => {
  const patientId = useDataProvider(DATA_KEYS.patient)?.id;
  const { hasCompletedSignup } = useDataProvider(DATA_KEYS.session);
  const { brand, flowType, treatmentCategory } = useFlowContext();

  const setFlashMessages = useSetAtom(FlashMessagesState.flashMessagesAtom);

  const handleErrors = useCallback(
    (error: CompleteSignupError) => {
      setFlashMessages((flashMessages) => [
        ...flashMessages,
        {
          children: error.response?.data?.message || 'An error occurred',
          icon: <ErrorIcon />,
          iconSize: 'sm',
          state: 'critical',
          title: 'Whoops!',
        },
      ]);

      logger.error('Complete Signup Error', {
        error: error.status,
        response: error.response?.data.message,
      });
    },
    [setFlashMessages],
  );

  const handlePromoApplied = useCallback(() => {
    setFlashMessages((flashMessages) => [
      ...flashMessages,
      {
        children:
          'Great! A $20 credit has been added to your account. It will apply automatically to your next order.',
        icon: <ThumbsUpIcon />,
        iconSize: 'sm',
        state: 'success',
        title: 'Success!',
      },
    ]);
  }, [setFlashMessages]);

  useEffect(() => {
    if (
      flowType === FlowType.MedicalHistoryUpdate ||
      flowType === FlowType.AgencyMedicalHistoryUpdate
    ) {
      FunnelService.completeMedicalHistoryUpdate(brand)
        .then((response) => {
          if (response?.data?.claimedPromo) {
            handlePromoApplied();
          }
        })
        .catch((error: CompleteSignupError) => {
          handleErrors(error);
        });

      trackEvent(AnalyticsEvent.MEDICAL_HISTORY_SURVEY_COMPLETED, {
        patientId,
      });
    } else {
      // If the user has already completed signup, we don't need to call the API again
      if (hasCompletedSignup) {
        return;
      }

      FunnelService.completeSignup(brand, flowType, treatmentCategory).catch(
        (error: CompleteSignupError) => {
          handleErrors(error);
        },
      );
    }
  }, [
    brand,
    flowType,
    handleErrors,
    handlePromoApplied,
    hasCompletedSignup,
    patientId,
    treatmentCategory,
  ]);

  return null;
};
